import { useContext, useState } from 'react'
import { Button } from '@which/seatbelt'
import { HeartIcon } from '@which/seatbelt/src/components/Icons'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import classNames from 'classnames'

import { saveApiUrl } from '../../../../shared/save-api-url'
import { ArticleTemplateContext } from '../../ArticleTemplateContext'
import styles from './Save.module.scss'

export const Save: React.FC<{ location: string }> = ({ location }) => {
  const { getToken } = useKindeAuth()
  const saveArticlesEnabled = useFeatureIsOn('feat-save-articles')
  const [isPending, setIsPending] = useState(false)

  const {
    meta: { contentId, title },
    savedStatus = { isSaved: false, id: null },
    setSavedStatus = () => {},
    setToast = () => {},
  } = useContext(ArticleTemplateContext)

  if (!saveArticlesEnabled) {
    return null
  }

  const BASE_URL = `${saveApiUrl()}/articles`

  const saveArticle = async (withTracking: boolean) => {
    setIsPending(true)
    try {
      if (withTracking) {
        dynamicGa4DataLayerPush({
          event: 'click_save',
          item_text: title,
          item_group: 'saved items',
          item_content_id: contentId,
          item_spec: `article ${location}`,
          action_group: 'add to save',
        })
      }

      const token = await getToken()
      const response = await fetch(BASE_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId: contentId }),
      })

      if (!response.ok) {
        throw new Error('Save failed')
      }

      const data = await response.json()
      setSavedStatus({ isSaved: true, id: data.id })
      setToast({
        displayToast: true,
        error: false,
        mainText: 'Article saved',
        linkText: 'View saved articles',
        linkUrl: '/saved',
      })
    } catch (e) {
      setToast({
        displayToast: true,
        error: true,
        mainText: "Sorry, we couldn't save this article.",
      })
    } finally {
      setIsPending(false)
    }
  }

  const removeSavedArticle = async () => {
    setIsPending(true)
    try {
      dynamicGa4DataLayerPush({
        event: 'click_save',
        item_text: title,
        item_group: 'saved items',
        item_content_id: contentId,
        item_spec: 'article',
        action_group: 'remove from save',
      })

      const token = await getToken()
      const response = await fetch(BASE_URL, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: savedStatus.id }),
      })

      if (!response.ok) {
        throw new Error('Delete failed')
      }

      setSavedStatus({ isSaved: false, id: null })
      setToast({
        displayToast: true,
        error: false,
        mainText: 'Article removed from saved items',
        linkText: 'undo',
        onClick: () => saveArticle(false),
      })
    } catch (e) {
      setToast({
        displayToast: true,
        error: true,
        mainText: 'Sorry, there was an error removing this article.',
      })
    } finally {
      setIsPending(false)
    }
  }

  const handleToggle = () => {
    if (isPending) {
      return
    }
    savedStatus.isSaved ? removeSavedArticle() : saveArticle(true)
  }

  return (
    <Button
      onClick={handleToggle}
      aria-pressed={savedStatus.isSaved}
      appearance="secondary"
      className={classNames(styles.saveButton, {
        [styles.saveButtonSaved]: savedStatus.isSaved,
      })}
      enableSpinner={isPending}
      disabled={isPending}
    >
      <HeartIcon selected={savedStatus.isSaved} width={16} height={16} />
      {savedStatus.isSaved ? 'Saved article' : 'Save article'}
    </Button>
  )
}
